// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import _ from 'lodash';
import { toast } from "sonner";
import { axios } from "./axios";
import { showError, showSuccess } from "../actions/globalActions";
import helpers from '@/utils/helpers';

const baseUrl = `${process.env.API_URL}/api/v1`;

const createAuthHeader = (token) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

// The PUT /games response returns POPULATED homeTeam/awayTeam and the grid
// (GameAdmin) renders names straight off the store row via getTeamDisplayName,
// so the store must KEEP the populated object — flattening it to an id string
// renders as "Unknown Team". When the response team lacks the `players`
// virtual (older API populate) but the previous row already had it for the
// same team, keep the previous object so zqtm_ teams keep showing player names.
const keepPopulatedTeam = (respTeam, prevTeam) => {
  if (!respTeam) return respTeam ?? null;
  const sameTeam = prevTeam && typeof prevTeam === 'object'
    && helpers.getObjId(prevTeam) === helpers.getObjId(respTeam);
  if (typeof respTeam !== 'object') return sameTeam ? prevTeam : respTeam;
  if (!respTeam.players && sameTeam && prevTeam.players) return prevTeam;
  return respTeam;
};

const useGameStore = create(immer((set) => ({
  games: [],

  // Methods to manipulate the state
  setGames: (games) => {
    set((state) => {
      state.games = games;
    });
  },

  // Asynchronous actions
  getGameById: async (id) => {
    try {
      const { data } = await axios.get(`${baseUrl}/games/${id}?include=homeTeam.players,awayTeam.players,organization,league`);
      set((state) => {
        state.games.push(data.result);
      });
    } catch (error) {
      console.error("Error in getGameById:", error);
    }
  },

  getGamesByTeamId: async (id) => {
    try {
      const { data } = await axios.get(`${baseUrl}/games/team/${id}?include=homeTeam,awayTeam,organization,league`);
      set((state) => {
        state.games = data.result;
      });
    } catch (error) {
      console.error("Error in getGamesByTeamId:", error);
    }
  },

  getGamesByDivisionId: async (id) => {
    try {
      const { data } = await axios.get(`${baseUrl}/games/division/${id}`);
      const remapped = data.result.map((game) => ({
        ...game,
        isHidden: game.isHidden ? "YES" : "NO",
        isCancelled: game.isCancelled ? "YES" : "NO",
      }));
      set((state) => {
        state.games = remapped;
      });
    } catch (error) {
      console.error("Error in getGamesByDivisionId:", error);
    }
  },

  getGamesByDivisionIds: async (divisionIds) => {
    console.log('🎮 GameStore: getGamesByDivisionIds called with:', divisionIds);
    try {
      if (!divisionIds || divisionIds.length === 0) {
        console.log('🎮 GameStore: No division IDs provided, clearing games');
        set((state) => {
          state.games = [];
        });
        return;
      }

      console.log('🎮 GameStore: Fetching games for divisions in parallel...');
      // Fetch games for all divisions in parallel
      const promises = divisionIds.map(id => {
        console.log(`🎮 GameStore: Creating promise for division ${id}`);
        return axios.get(`${baseUrl}/games/division/${id}`)
      });
      
      const responses = await Promise.all(promises);
      console.log('🎮 GameStore: Received responses:', responses.length);
      
      // Combine all games from all divisions
      const allGames = responses.flatMap(response => {
        console.log(`🎮 GameStore: Processing response with ${response.data.result.length} games`);
        return response.data.result;
      });
      
      console.log('🎮 GameStore: Total games combined:', allGames.length);
      
      const remapped = allGames.map((game) => ({
        ...game,
        isHidden: game.isHidden ? "YES" : "NO",
        isCancelled: game.isCancelled ? "YES" : "NO",
      }));
      
      console.log('🎮 GameStore: Setting games in store:', remapped.length);
      set((state) => {
        state.games = remapped;
      });
    } catch (error) {
      console.error("🎮 GameStore: Error in getGamesByDivisionIds:", error);
    }
  },
  getGamesBySeasonId: async (id, cb) => {
    try {
      console.log('getGamesBySeasonId::id::', id);
      const { data } = await axios.get(`${baseUrl}/games/season/${id}?include=homeTeam,awayTeam,organization,league,division`);
      const remapped = data.result.map((game) => ({ 
        ...game,
        isHidden: game.isHidden ? "YES" : "NO",
        isCancelled: game.isCancelled ? "YES" : "NO",
      }));
      set((state) => {
        state.games = remapped;
      });
      if (cb) cb(remapped);
    } catch (error) {
      console.error("Error in getGamesBySeasonId:", error);
    }
  },

  getGamesWithFilters: async (params = {}) => {
    try {
      const { data } = await axios.get(`${baseUrl}/games/?${helpers.jsonToQueryString(params)}`);
      set((state) => {
        state.games = data.result;
      });
    } catch (error) {
      console.error("Error in getGamesWithFilters:", error);
    }
  },

  updateGame: (getToken) => {
    return async (game, cb) => {
      try {
        const token = await getToken();
        const payload = {
          ...game,
          isHidden: game.isHidden === 'YES' ? true : game.isHidden === 'NO' ? false : !!game.isHidden,
          isCancelled: game.isCancelled === 'YES' ? true : game.isCancelled === 'NO' ? false : !!game.isCancelled,
          homeTeam: helpers.getObjId(game.homeTeam),
          awayTeam: helpers.getObjId(game.awayTeam),
          locationModel: helpers.getObjId(game.locationModel),
          timeSlot: helpers.getObjId(game.timeSlot),
          organization: helpers.getObjId(game.organization),
          league: helpers.getObjId(game.league),
          division: helpers.getObjId(game.division),
        };
        const { data } = await axios.put(`${baseUrl}/games/d/${game._id}`, payload, createAuthHeader(token));
        const responseError = data?.error;
        responseError ? toast.error(responseError) : toast.success(`Successfully Updated`);
        set((state) => {
          const index = state.games.findIndex(g => g._id === game._id);
          if (index !== -1) {
            const prev = state.games[index];
            state.games[index] = {
              ...data.result,
              isHidden: data.result.isHidden ? "YES" : "NO",
              isCancelled: data.result.isCancelled ? "YES" : "NO",
              homeTeam: keepPopulatedTeam(data.result.homeTeam, prev.homeTeam),
              awayTeam: keepPopulatedTeam(data.result.awayTeam, prev.awayTeam),
              locationModel: helpers.getObjId(data.result.locationModel),
              timeSlot: helpers.getObjId(data.result.timeSlot),
            };
          }
        });
        if (cb) cb(game._id);
      } catch (error) {
        showError("Error", "Update Error");
        console.error("Error in updateGame:", error);
      }
    }
  },

  addGame: (getToken) => {
    return async (game) => {
      try {
        const token = await getToken();
        const { data, error } = await axios.post(`${baseUrl}/games/d`, game, createAuthHeader(token));
        error ? toast.error(error) : toast.success(`Successfully Updated`);
        set((state) => {
          state.games.push(data.result);
        });
      } catch (error) {
        showError("Error", "Add Error");
        console.error("Error in addGame:", error);
      }
    }
  },
  addBulkGames: (getToken) => {
    return async (games) => {
      try {
        const token = await getToken();
        const { data, error } = await axios.post(`${baseUrl}/games/d/bulk`, games, createAuthHeader(token));
        error ? toast.error(error) : toast.success(`Successfully Updated`);
        set((state) => {
          state.games.push(data.result);
        });
        // Callers must be able to tell success from failure. This used to
        // swallow errors entirely (catch → toast → resolve), so the bulk-import
        // page reported "Imported N games" and navigated away after a POST that
        // never wrote anything — a 401 from an expired session produced a false
        // success on an 848-game import.
        return { error: error || null, result: data.result };
      } catch (error) {
        showError("Error", "Add Error");
        console.error("Error in addBulkGames:", error);
        return { error: error?.response?.data?.error || error?.message || 'Bulk create failed' };
      }
    }
  },

  deleteGame: (getToken) => {
    // opts.silent suppresses the per-game success toast — used for bulk
    // cleanup (e.g. clearing a division's generated games on a format change)
    // so it doesn't fire dozens of toasts.
    return async (gameId, opts = {}) => {
      try {
        const token = await getToken();
        const { data, error } = await axios.delete(`${baseUrl}/games/d/${gameId}`, createAuthHeader(token));
        if (error) toast.error(error);
        else if (!opts.silent) toast.success(`Successfully Removed`);
        set((state) => {
          state.games = state.games.filter(g => g._id !== gameId);
        });
      } catch (error) {
        console.error("Error in deleteGame:", error);
      }
    }
  },

})));

export default useGameStore;
